import React, { useState } from 'react';
import { App as AntApp, Button, Flex, Input, Modal, Typography } from 'antd';
import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import { isDevAuthMock, signOutUser } from '../../auth/index.js';
import { deleteOwnAccount, downloadBlob, exportOwnData } from './billingApi.js';

const { Text } = Typography;

/**
 * "Export my data" and "Delete my account", in Settings > Account and on the lock screen. Both
 * work whether or not payments are switched on. The simulated development sign-in has no server:
 * the export is a small stand-in and deleting only signs out.
 */
export const AccountActions: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { message } = AntApp.useApp();
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = isDevAuthMock()
        ? new Blob([JSON.stringify({ note: 'Simulated development export' }, null, 2)], {
            type: 'application/json',
          })
        : await exportOwnData();
      downloadBlob(blob, `lingoping-export-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (err) {
      console.error('Export failed:', err);
      message.error("Your data couldn't be exported. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setTyped('');
    setDeleteError(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      if (!isDevAuthMock()) await deleteOwnAccount();
      // The account is gone: forget it on this device too (the page then starts afresh).
      await signOutUser({ discardUnsent: true });
      window.location.reload();
    } catch (err) {
      console.error('Deleting the account failed:', err);
      setDeleteError("Your account couldn't be deleted. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <>
      <Flex gap={8} wrap>
        <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExport}>
          Export my data
        </Button>
        <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteOpen(true)}>
          Delete my account
        </Button>
        {children}
      </Flex>

      <Modal
        title="Delete my account"
        open={deleteOpen}
        onCancel={closeDelete}
        onOk={handleDelete}
        okText="Delete everything"
        okButtonProps={{ danger: true, disabled: typed !== 'DELETE', loading: deleting }}
        cancelButtonProps={{ disabled: deleting }}
        cancelText="Keep my account"
        destroyOnHidden
      >
        <Flex vertical gap={10} style={{ padding: '8px 0' }}>
          <Text>
            Your cards and progress are deleted for good. A running subscription is cancelled.
          </Text>
          <label htmlFor="delete-confirm">
            Type <strong>DELETE</strong> to confirm
          </label>
          <Input
            id="delete-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onPressEnter={() => typed === 'DELETE' && void handleDelete()}
            autoComplete="off"
            autoFocus
          />
          {deleteError && (
            <Text type="danger" role="alert">
              {deleteError}
            </Text>
          )}
        </Flex>
      </Modal>
    </>
  );
};
